import { useEffect } from 'react';
import logger from '../utils/Logger';
import {getCookie,setCookie} from '../utils/cookieUtils';

export function useSessionRestore(deps) {
  useEffect(() => {

    const pathSegments = window.location.pathname.split('/');

    const urlConvId = pathSegments[pathSegments.length - 1];

    const hasConversationInUrl = urlConvId && urlConvId !== 'record' && urlConvId.length > 10;

    // =========================
    // ACTIVE SESSION CHECK
    // =========================

    const existingActive = getCookie('conversation_active');

    const existingStatus = getCookie('recording_status');

    const hasActiveSession = existingActive === 'true' || existingStatus === '🎙️ Listening...' || existingStatus === 'Paused';
    const active = getCookie('conversation_active');
    const convId = getCookie('conversation_id');
    // =========================
    // RESTORE ACTIVE SESSION
    // =========================
    if (active === 'true' && convId) {
      deps.isNewSessionRef.current = false;
      const sessionState = deps.getSessionStateFromCookies();
      if (sessionState.startTime && sessionState.status) {
        const originalSessionId = getCookie('original_session_id');
        if (!originalSessionId) {
          deps.clearSessionState();
          setCookie('conversation_active','false');
          return;
        }
        deps.setSessionId(originalSessionId);
        deps.setConversationId(convId);
        deps.setShareLink(`${window.location.origin}/watch/${convId}`);
        // =========================
        // STATUS RESTORE
        // =========================
        if (sessionState.status === 'Paused') {
          deps.setStatus('Paused');
          deps.setIsPaused(true);
          deps.isPausedRef.current = true;
        } else {
          deps.setStatus(sessionState.status);
          deps.setIsPaused(sessionState.isPaused);
          deps.isPausedRef.current = sessionState.isPaused;
        }
        // =========================
        // TIMER RESTORE
        // =========================
        const elapsedTime = deps.calculateElapsedTime();

        deps.setTimer(elapsedTime);

        // =========================
        // DATE/TIME RESTORE
        // =========================

        if (
          !deps.dateString ||
          !deps.timeString
        ) {
          const now = new Date();
          deps.setDateString(now.toLocaleDateString());
          deps.setTimeString(
            now.toLocaleTimeString('en-US',
                {
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: true
                }
              )
              .toUpperCase()
          );
        }

        // =========================
        // TRANSCRIPT RESTORE
        // =========================

        deps.fetchHistoricalTranscripts(originalSessionId, convId)
        .then(historicalSegments => {
          if (
            historicalSegments.length > 0
          ) {
            logger.info(
              'Historical transcripts restored successfully',
              {
                component: 'useSessionRestore',

                action: 'SESSION_TRANSCRIPTS_RESTORED',
                userId: deps.getAuthenticatedUserId(),
                context: {
                    sessionId: originalSessionId,
                    conversationId: convId,
                    segmentsCount: historicalSegments.length,

                  lastDivId: historicalSegments[
                    historicalSegments.length - 1
                    ]?.div_id || null
                }
              }
            );

            deps.setTranscriptSegments(historicalSegments);
            const lastSegment = historicalSegments[historicalSegments.length - 1];

            if (lastSegment && lastSegment.div_id) {
              deps.setCurrentDivId(lastSegment.div_id);
              deps.currentDivIdRef.current = lastSegment.div_id;
              const newSequenceMap = {};
              newSequenceMap[lastSegment.div_id] = lastSegment.sequence_id + 1;
              deps.setSequenceMap(newSequenceMap);
              deps.sequenceMapRef.current = newSequenceMap;
            }

          } else {

            logger.info(
              'No historical transcripts found during session restore',
              {
                component: 'UseSessionRestore',
                action: 'SESSION_NO_TRANSCRIPTS',
                userId: deps.getAuthenticatedUserId(),
                context: {
                  sessionId: originalSessionId,
                  conversationId: convId
                }
              }
            );
          }

        })

        .catch(error => {

          logger.error(
            'Failed to fetch historical transcripts during session restore',
            {
              component: 'UseSessionRestore',

              action:'SESSION_TRANSCRIPTS_FETCH_FAILED',
              userId:deps.getAuthenticatedUserId(),
              context: {
                sessionId:originalSessionId,
                conversationId:convId
              },
              error
            }
          );
        });

        // =========================
        // TOKEN REFRESH
        // =========================

        if (sessionState.status === '🎙️ Listening...' || sessionState.status === 'Paused') {
          setTimeout(() => {
            if (deps.socketRef.current && deps.socketRef.current.readyState === WebSocket.OPEN) {
              deps.requestToken("refresh");
            }
          }, 500);
        }

      } else {

        deps.clearSessionState();
        setCookie('conversation_active','false');
      }

      return;
    }

    // =========================
    // FALLBACK RESTORE
    // =========================

    const recordingStatus = getCookie('recording_status');
    const recordingStartTime = getCookie('recording_start_time');

    const isPausedFromCookie = getCookie('is_recording_paused') === 'true';

    if (recordingStartTime) {

      const elapsedTime = deps.calculateElapsedTime();
      deps.setTimer(elapsedTime);
      if (isPausedFromCookie || recordingStatus === 'Paused') {
        deps.setStatus('Paused');
        deps.setIsPaused(true);

        deps.isPausedRef.current = true;

        deps.saveRecordingStatus('Paused');

      } else if (recordingStatus === '🎙️ Listening...') {

        deps.setStatus('🎙️ Listening...');

        deps.setIsPaused(false);

        deps.isPausedRef.current = false;
      }

      const fallbackConvId = getCookie('conversation_id');

      if (fallbackConvId) {
        deps.setShareLink(`${window.location.origin}/watch/${fallbackConvId}`);
      }

      if (!deps.dateString || !deps.timeString) {

        const now = new Date();

        deps.setDateString(now.toLocaleDateString());

        deps.setTimeString(now.toLocaleTimeString(
              'en-US',
              {
                hour: '2-digit',
                minute: '2-digit',
                hour12: true
              }
            )
            .toUpperCase()
        );
      }
      return;
    }

    // =========================
    // DEFAULT CLEANUP
    // =========================

    const sessionId = getCookie('session_id');
    if (recordingStatus || sessionId) {
      deps.clearSessionState();
    }

    // =========================
    // NEW SESSION NAVIGATION
    // =========================

    if (deps.conversationId && !active) {

      const originalSessionId = getCookie('original_session_id');
      if (originalSessionId) {
        const recordingStatus = getCookie('recording_status');

        const isPausedFromCookie = getCookie('is_recording_paused') === 'true';

        deps.setSessionId(originalSessionId);

        deps.setConversationId(deps.conversationId);
        deps.setShareLink(`${window.location.origin}/watch/${deps.conversationId}`);
        if (recordingStatus === 'Paused' || isPausedFromCookie) {
          deps.setStatus('Paused');
          deps.setIsPaused(true);
          deps.isPausedRef.current = true;
          deps.saveRecordingStatus( 'Paused');
        } else if (recordingStatus === '🎙️ Listening...') {
          deps.setStatus('🎙️ Listening...');
          deps.setIsPaused(false);
          deps.isPausedRef.current = false;
          deps.saveRecordingStatus('🎙️ Listening...');
        } else {
          deps.setStatus('🎙️ Listening...');
          deps.setIsPaused(false);
          deps.isPausedRef.current = false;
          deps.saveRecordingStatus('🎙️ Listening...');
        }

        setCookie('conversation_active', 'true');

        const now = new Date();

        deps.setDateString(now.toLocaleDateString());
        deps.setTimeString(now.toLocaleTimeString(
              'en-US',
              {
                hour: '2-digit',
                minute: '2-digit',
                hour12: true
              }
            )
            .toUpperCase()
        );

        deps.setTranscriptSegments([]);

        deps.setPersistentSegments([]);
      }
    }

    // =========================
    // DEFAULT DATE/TIME
    // =========================

    if (!deps.dateString ||!deps.timeString) {
      const now = new Date();
      deps.setDateString(now.toLocaleDateString());
      deps.setTimeString(
        now
          .toLocaleTimeString(
            'en-US',
            {
              hour: '2-digit',
              minute: '2-digit',
              hour12: true
            }
          )
          .toUpperCase()
      );
    }

  }, [
    deps.conversationId,
    deps.dateString,
    deps.timeString,
    deps.setSessionId,
    deps.setShareLink,
    deps.setStatus,
    deps.setIsPaused,
    deps.setTimer,
    deps.setDateString,
    deps.setTimeString,
    deps.setTranscriptSegments
  ]);
}